import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { onValue, ref } from 'firebase/database';
import { httpsCallable } from 'firebase/functions';
import { Building2, AlertCircle, CheckCircle2, Lock, Mail, User, Search, ArrowRight } from 'lucide-react';
import { rtdb, functions } from '../../firebase';
import './establishment.css';

function RequestAccessPage() {
  const [crousList, setCrousList] = useState([]);
  const [loadingCrous, setLoadingCrous] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());

  const [form, setForm] = useState({
    displayName: '',
    establishmentName: '',
    email: '',
    password: '',
    passwordConfirm: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    return onValue(ref(rtdb, 'crous'), (snap) => {
      const val = snap.val() || {};
      const list = Object.entries(val)
        .map(([id, d]) => ({
          id,
          name: (d && d.name) || id,
          campus: (d && d.campus) || '',
          city: (d && (d.city || d.region)) || ''
        }))
        .sort((a, b) => a.name.localeCompare(b.name, 'fr'));
      setCrousList(list);
      setLoadingCrous(false);
    });
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return crousList;
    return crousList.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.campus.toLowerCase().includes(q) ||
        c.id.toLowerCase().includes(q)
    );
  }, [search, crousList]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (form.password !== form.passwordConfirm) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }
    if (form.password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    if (selected.size === 0) {
      setError('Sélectionne au moins un RU à gérer.');
      return;
    }

    setSubmitting(true);
    try {
      const fn = httpsCallable(functions, 'requestEstablishmentAccount');
      const res = await fn({
        email: form.email,
        password: form.password,
        displayName: form.displayName,
        establishmentName: form.establishmentName,
        crousIds: Array.from(selected)
      });
      setSuccess(res.data);
    } catch (err) {
      setError(err.message || 'Une erreur est survenue. Réessaie plus tard.');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="est-login-page">
        <div className="est-login-card est-login-card-wide">
          <div className="est-login-head">
            <div className="est-login-icon"><CheckCircle2 size={28} /></div>
            <h1>Demande envoyée</h1>
            <p>
              L'équipe CrousRadar a reçu ta demande. Tu recevras bientôt un code
              d'activation à 6 chiffres par le canal convenu.
            </p>
          </div>
          <div className="est-alert est-alert-ok" style={{ marginBottom: 20 }}>
            <CheckCircle2 size={16} />
            <span>
              Dès que tu as ton code, rends-toi sur la page d'activation pour finaliser
              l'accès à ton Espace Établissement.
            </span>
          </div>
          <Link to="/etablissement/activation" className="est-btn est-btn-primary" style={{ justifyContent: 'center' }}>
            Activer mon compte <ArrowRight size={16} />
          </Link>
          <p className="est-login-footnote" style={{ marginTop: 20 }}>
            Pas reçu le code sous 48 h ? <a href="mailto:contact@crousradar.fr">contact@crousradar.fr</a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="est-login-page est-request-page">
      <div className="est-login-card est-login-card-wide">
        <div className="est-login-head">
          <div className="est-login-icon"><Building2 size={28} /></div>
          <h1>Demande d'accès partenaire</h1>
          <p>
            Réservé aux gestionnaires de restaurants universitaires CROUS. Ta demande est
            examinée manuellement : tu recevras un code d'activation avant de pouvoir
            piloter ton espace.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="est-login-form">
          <div className="est-form-grid-2">
            <label className="est-field">
              <span>Nom complet</span>
              <div className="est-input-wrap">
                <User size={16} />
                <input
                  type="text"
                  required
                  minLength={2}
                  value={form.displayName}
                  onChange={(e) => setForm({ ...form, displayName: e.target.value })}
                />
              </div>
            </label>
            <label className="est-field">
              <span>Nom de l'établissement</span>
              <div className="est-input-wrap">
                <Building2 size={16} />
                <input
                  type="text"
                  required
                  minLength={2}
                  value={form.establishmentName}
                  onChange={(e) => setForm({ ...form, establishmentName: e.target.value })}
                  placeholder="CROUS de Rennes"
                />
              </div>
            </label>
          </div>

          <label className="est-field">
            <span>Email professionnel</span>
            <div className="est-input-wrap">
              <Mail size={16} />
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="prenom.nom@univ-rennes.fr"
              />
            </div>
          </label>

          <div className="est-form-grid-2">
            <label className="est-field">
              <span>Mot de passe (8 caractères min.)</span>
              <div className="est-input-wrap">
                <Lock size={16} />
                <input
                  type="password"
                  required
                  minLength={8}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                />
              </div>
            </label>
            <label className="est-field">
              <span>Confirmation du mot de passe</span>
              <div className="est-input-wrap">
                <Lock size={16} />
                <input
                  type="password"
                  required
                  minLength={8}
                  value={form.passwordConfirm}
                  onChange={(e) => setForm({ ...form, passwordConfirm: e.target.value })}
                />
              </div>
            </label>
          </div>

          <div className="est-field">
            <span>RU à piloter ({selected.size} sélectionné{selected.size > 1 ? 's' : ''})</span>
            <div className="est-crous-picker">
              <div className="est-search est-crous-search">
                <Search size={16} />
                <input
                  type="search"
                  placeholder="Filtrer par nom, ville, campus…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              {loadingCrous ? (
                <div className="est-empty est-empty-small">Chargement des RU…</div>
              ) : (
                <div className="est-crous-list">
                  {filtered.length === 0 && (
                    <div className="est-empty est-empty-small">Aucun RU ne correspond.</div>
                  )}
                  {filtered.map((c) => {
                    const active = selected.has(c.id);
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => toggle(c.id)}
                        className={`est-crous-option ${active ? 'is-selected' : ''}`}
                      >
                        <div>
                          <strong>{c.name}</strong>
                          <span>{c.campus || c.city || c.id}</span>
                        </div>
                        {active && <CheckCircle2 size={18} />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {error && (
            <div className="est-alert est-alert-error">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            className="est-btn est-btn-primary"
            disabled={submitting}
            style={{ justifyContent: 'center' }}
          >
            {submitting ? 'Envoi en cours…' : 'Envoyer la demande'}
          </button>

          <p className="est-login-footnote">
            Déjà un code d'activation ?{' '}
            <Link to="/etablissement/activation">Activer mon compte</Link>
          </p>
        </form>
      </div>
    </div>
  );
}

export default RequestAccessPage;
